import * as React from 'react';

import uniq from 'lodash/uniq';
import { LucideChevronDown, LucideLoader, LucideX } from 'lucide-react';
import useResizeObserver from 'use-resize-observer';

import { Col, Label, MaybeEmpty, Popover, Row } from '..';
import { cn } from '../../../lib';
import { fieldGroupVariants } from '../Field';

import { SelectableOption } from './SelectableOption';

type SelectedOptionProps = {
  label: string;
  onClickRemove: (e: React.MouseEvent) => void;
  extraInfo?: React.ReactNode | null;
};

const SelectedOption = ({
  label,
  extraInfo,
  onClickRemove,
}: SelectedOptionProps) => (
  <div className="@SelectedOption flex items-stretch rounded-sm border border-neutral/10 bg-neutral/5 text-sm">
    <div className="inline-flex gap-1 px-[0.5em] py-1 whitespace-nowrap">
      {label} {extraInfo}
    </div>
    <div
      className="flex min-h-[22px] items-center rounded-l-none rounded-r-sm px-1 text-neutral hover:bg-failure/10 hover:text-failure"
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onClick={onClickRemove}
    >
      <LucideX className="size-[12px] opacity-50" />
    </div>
  </div>
);

export type SelectableOptionProps<Key> = {
  key: Key;
  label: string | null;
  extraInfo?: React.ReactNode | null;
  metadata?: unknown;
  isDisabled?: boolean;
};

export type MultiSelectProps<Key, Option extends SelectableOptionProps<Key>> = {
  className?: string;
  autoFocus?: boolean;
  isDisabled?: boolean;
  label?: string;
  required?: boolean;
  options?: Option[];
  selectedKeys?: Key[];
  defaultSelectedKeys?: Key[];
  isLoading?: boolean;
  onSelectionChange?: (value: Key[]) => void;
  onFocus?: (e: React.FocusEvent) => void;
  onBlur?: (e: React.FocusEvent) => void;
  placeholder?: string;
  onSearch?: (query: string) => void;
  customInput?: (selectedOptions?: Option[]) => React.ReactNode;
};

type MultiSelectRef = {
  focus: () => void;
};

export const MultiSelect = React.forwardRef(function MultiSelect<
  Key,
  Option extends SelectableOptionProps<Key>,
>(
  {
    label,
    required,
    options,
    selectedKeys,
    onSelectionChange,
    autoFocus,
    isDisabled,
    onFocus,
    onBlur,
    placeholder,
    className,
    isLoading,
    onSearch,
    customInput,
  }: MultiSelectProps<Key, Option>,
  ref: React.Ref<MultiSelectRef>,
) {
  const inputRef = React.useRef<HTMLSpanElement>(null);
  const buttonContainerRef = React.useRef<HTMLDivElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);

  React.useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        inputRef.current?.focus();
      },
    }),
    [],
  );

  const [query, setQuery] = React.useState('');
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [currentFocusedIndex, setCurrentFocusedIndex] = React.useState(0);
  const [displaySelectedKeys, setDisplaySelectedKeys] = React.useState<Key[]>(
    selectedKeys ?? [],
  );

  const trimmedQuery = query.trim().toLowerCase();

  const selectedOptions = selectedKeys
    ?.map((it) => options?.find((x) => x.key === it))
    .filter((option): option is Option => option != null);

  const availableOptions = React.useMemo(() => {
    const filtered =
      options?.filter(
        (it) => !trimmedQuery || it.label?.toLowerCase().includes(trimmedQuery),
      ) ?? [];

    return filtered.sort((a, b) => {
      const aIsSelected = displaySelectedKeys?.includes(a.key) ?? false;
      const bIsSelected = displaySelectedKeys?.includes(b.key) ?? false;

      return Number(bIsSelected) - Number(aIsSelected);
    });
  }, [options, trimmedQuery, displaySelectedKeys]);

  const { width } = useResizeObserver({
    ref: buttonContainerRef as never,
  });

  const updateSearchTerm = React.useCallback(
    (query: string) => {
      setQuery(query);
      onSearch?.(query);
    },
    [onSearch],
  );

  const handleBackspaceKey = React.useCallback(() => {
    if (!trimmedQuery) {
      onSelectionChange?.(selectedKeys?.slice(0, -1) as Key[]);
    }
  }, [onSelectionChange, trimmedQuery, selectedKeys]);

  const handleEnterKey = React.useCallback(() => {
    if (isMenuOpen) {
      const currentOption = availableOptions[currentFocusedIndex];

      if (selectedKeys?.includes(currentOption.key)) {
        onSelectionChange?.(
          selectedKeys
            ?.filter((x) => x !== currentOption.key)
            .filter(Boolean) as Key[],
        );
      } else {
        onSelectionChange?.(
          [...(selectedKeys ?? []), currentOption.key].filter(Boolean) as Key[],
        );
      }

      updateSearchTerm('');
    }
  }, [
    isMenuOpen,
    currentFocusedIndex,
    availableOptions,
    selectedKeys,
    onSelectionChange,
    updateSearchTerm,
  ]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (isMenuOpen) {
            setCurrentFocusedIndex(
              Math.min(availableOptions.length - 1, currentFocusedIndex + 1),
            );
          } else {
            setIsMenuOpen(true);
            setCurrentFocusedIndex(0);
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          setCurrentFocusedIndex(Math.max(0, currentFocusedIndex - 1));
          break;

        case 'Enter':
          e.preventDefault();
          handleEnterKey();
          break;

        case 'Backspace':
          handleBackspaceKey();
          break;

        case 'Tab':
          updateSearchTerm('');
          setIsMenuOpen(false);
          break;

        case 'Escape':
          e.stopPropagation();
          updateSearchTerm('');
          setIsMenuOpen(false);
          break;
      }
    },
    [
      availableOptions.length,
      currentFocusedIndex,
      handleBackspaceKey,
      handleEnterKey,
      isMenuOpen,
      updateSearchTerm,
    ],
  );

  const handleFieldContainerClick = React.useCallback(() => {
    setIsMenuOpen(!isMenuOpen);
    inputRef.current?.focus();
  }, [isMenuOpen]);

  const handleFieldContainerBlur = React.useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      if (!popoverRef.current?.contains(e.relatedTarget as Node)) {
        setIsMenuOpen(false);
        setCurrentFocusedIndex(0);
        onBlur?.(e);
      } else {
        (e.target as HTMLDivElement).focus();
      }
      updateSearchTerm('');
    },
    [onBlur, updateSearchTerm],
  );

  const handleSearchInput = React.useCallback(
    (e: React.FormEvent<HTMLSpanElement>) => {
      const searchInputElement = e.currentTarget;
      const searchInputText = searchInputElement.innerText;
      setIsMenuOpen(!!searchInputText);

      updateSearchTerm(searchInputText.trim() ?? '');
    },
    [updateSearchTerm],
  );

  const handleSearchInputKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLSpanElement>) => {
      if (e.key === 'Backspace') handleBackspaceKey();
    },
    [handleBackspaceKey],
  );

  React.useEffect(() => {
    setCurrentFocusedIndex(0);
  }, [trimmedQuery]);

  React.useEffect(() => {
    if (query === '' && inputRef.current) {
      inputRef.current.innerText = query;
    }
  }, [query]);

  React.useEffect(() => {
    if (!isMenuOpen) {
      setDisplaySelectedKeys(selectedKeys ?? []);
    }
  }, [selectedKeys, isMenuOpen]);

  return (
    <div className="@MultiSelect contents">
      <div className="flex h-full flex-col gap-2" ref={buttonContainerRef}>
        {label && <Label aria-required={required}>{label}</Label>}
        <div
          tabIndex={isDisabled ? undefined : -1}
          className={cn(
            fieldGroupVariants({
              disabled: isDisabled,
            }),
            'h-auto min-h-20 max-w-full px-3 py-1',
            selectedOptions?.length && 'pl-1',
            className,
          )}
          onFocus={onFocus}
          onBlur={handleFieldContainerBlur}
          onKeyDown={handleKeyDown}
          onClick={!isDisabled ? handleFieldContainerClick : undefined}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Row gap1 className="flex-wrap">
            {customInput
              ? customInput(selectedOptions)
              : selectedOptions?.map((it) => (
                  <SelectedOption
                    key={String(it?.key)}
                    label={it?.label ?? ''}
                    extraInfo={it?.extraInfo}
                    onClickRemove={(e) => {
                      e.stopPropagation();
                      e.preventDefault();

                      setIsMenuOpen(false);
                      onSelectionChange?.(
                        selectedKeys
                          ?.filter((x) => x !== it?.key)
                          .filter(Boolean) as Key[],
                      );
                    }}
                  />
                ))}
            {!selectedOptions?.length && !query && (
              <span className="absolute text-neutral/40">
                {placeholder ?? 'Select'}
              </span>
            )}
            <span
              ref={inputRef}
              contentEditable={!isDisabled}
              role="textbox"
              className="min-w-[5px] text-neutral/40 outline-none"
              autoFocus={autoFocus}
              onInput={handleSearchInput}
              onKeyDown={handleSearchInputKeyDown}
              suppressContentEditableWarning
            />
          </Row>
          {isLoading ? (
            <LucideLoader
              className={'ml-auto size-[1.125em] animate-spin text-neutral/40'}
            />
          ) : (
            <LucideChevronDown className="ml-auto size-[16px] shrink-0 text-neutral/40" />
          )}
        </div>
      </div>
      {!isDisabled && (
        <Popover
          ref={popoverRef}
          isOpen={isMenuOpen}
          isNonModal
          onOpenChange={setIsMenuOpen}
          placement="bottom left"
          style={{ width }}
          triggerRef={buttonContainerRef}
          offset={2}
          className="no-scrollbar flex max-h-[40vh] overflow-y-auto py-0.5"
        >
          <Col gap1 className={'w-full p-0.5'}>
            <MaybeEmpty
              isEmpty={availableOptions.length === 0}
              size="sm"
              title="No options found"
            >
              {availableOptions.map((it, i) => (
                <SelectableOption
                  isDisabled={it.isDisabled}
                  isFocused={currentFocusedIndex === i}
                  isSelected={selectedKeys?.includes(it.key)}
                  onMouseEnter={() => setCurrentFocusedIndex(i)}
                  key={String(it.key)}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();

                    const isAlreadySelected = selectedKeys?.includes(it.key);
                    const newSelectedKeys = isAlreadySelected
                      ? (selectedKeys?.filter((x) => x !== it.key) ?? [])
                      : uniq([...(selectedKeys ?? []), it.key]);

                    onSelectionChange?.(newSelectedKeys);
                    updateSearchTerm('');
                  }}
                >
                  <span className="w-full flex-1">{it.label}</span>
                  {it.extraInfo && (
                    <span className="shrink-0 self-end">{it.extraInfo}</span>
                  )}
                </SelectableOption>
              ))}
            </MaybeEmpty>
          </Col>
        </Popover>
      )}
    </div>
  );
}) as <Key, Option extends SelectableOptionProps<Key>>(
  props: MultiSelectProps<Key, Option> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement;
